<script setup>
import { onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';

/** API */
import { addContentLogAPI } from '@/api/logServiceApi';
import {
  getDailyFortuneDetailAPI,
  purchaseDailyCategoryAPI,
  purchaseDailyFortuneAPI,
  getDailyCategoryFreeCountAPI,
  getDailyFortuneCheckAPI,
  setMySajuAPI,
} from '@/api/calendarApi';

/** store, utils */
import { DEV_MODE } from '@/config/dev-mode';
import { usePaymentsStore } from '@/store/modules/payments';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { convertViewTitleToYYYYMM } from '@/utils/calendarUtil';
import { formatDate } from '@/utils/dateFormatter';

/** composables */
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDialogManager } from '@/composables/useDialogManager';
import { useSajuClock } from '@/composables/calendar/useSajuClock';
import { useMySaju, clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import { useMySajuValidator } from '@/composables/calendar/mySaju/useMySajuValidator';
import { useParsedMySajuInfo } from '@/composables/calendar/mySaju/useParsedMySajuInfo';
import { useServicePrice } from '@/composables/calendar/useServicePrice';
import { usePurchaseFortune } from '@/composables/calendar/usePurchaseFortune';
import { useCalendarValidation } from '@/composables/calendar/useCalendarValidation';
import { useCalendar } from '@/composables/calendar/useCalendar';
import { useGesture } from '@/composables/calendar/useGesture';
import { useDailyFortune } from '@/composables/calendar/dailyFortuneDrawer/useDailyFortune';
import { useParsedDailyFortune } from '@/composables/calendar/dailyFortuneDrawer/useParsedDailyFortune';
import { useCustomScrollLock } from '@/composables/useCustomScrollLock';
import { useBackLayer } from '@/composables/useAppBack';

/** 컴포넌트 */
import FullCalendar from '@fullcalendar/vue3';
import CreditBar from '@/components/calendar/CreditBar.vue';
import DailyFortuneDrawer from '@/components/calendar/drawer/DailyFortuneDrawer.vue';
import SajuClockPopup from '@/components/calendar/SajuClockPopup.vue';
import CalendarSidebar from '@/components/calendar/CalendarSidebar.vue';
import CalendarHeader from '@/components/calendar/CalendarHeader.vue';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import PointConfirm from '@/components/calendar/PointConfirm.vue';
import LoadingIndicator from '@/components/common/LoadingIndicator.vue';
import IconCalendarFill from '@/assets/icon/IconCalendarFill.vue';
import PopPurchaseMonthly from '@/components/calendar_mobile/PopPurchaseMonthly.vue';
import PopPurchaseMonthlyMobile from '@/components/calendar_mobile/PopPurchaseMonthlyMobile.vue';
import DailyFortunePaymentPopup from '@/components/calendar/popup/DailyFortunePaymentPopup.vue';
import Snackbar from '@/components/common/Snackbar.vue';

/** 모바일 반응형 컴포넌트 */
import CalendarHeaderMobile from '@/components/calendar/CalendarHeaderMobile.vue';
import CalendarBottomLabel from '@/components/calendar_mobile/CalendarBottomLabel.vue';
import MobileCalendarDrawer from '@/components/calendar_mobile/drawer/MobileCalendarDrawer.vue';
import MobileCalendarDrawerHeader from '@/components/calendar_mobile/drawer/MobileCalendarDrawerHeader.vue';
import MobileCalendarDrawerBody from '@/components/calendar_mobile/drawer/MobileCalendarDrawerBody.vue';
import PopCalendarHowtoMobile from '@/components/calendar_mobile/PopCalendarHowtoMobile.vue';
import PopCalendarUsageMobile from '@/components/calendar_mobile/PopCalendarUsageMobile.vue';

/** 스타일 */
import '@/assets/nangman_style/calendar/calendar_web.scss';
import '@/assets/nangman_style/calendar/calendar_mobile.scss';
import MobileHeader from '@/components/common/MobileHeader.vue';

definePage({ meta: { layout: 'manse', title: '낭만일진' } });

const store = useStore();
const userId = computed(() => store.getters['user/id']);

const paymentsStore = usePaymentsStore();
const primarySajuStore = usePrimarySajuStore();

const route = useRoute();
const router = useRouter();

applySeoMeta({
  title: '무료 오늘의운세, 애정운·건강운·인간관계운',
  description:
    '매일 새로 보는 오늘의 운세. 애정운·건강운·인간관계운 등 내 사주 기반 맞춤 운세를 무료로 확인하세요. 재물운 등 심층 운세는 유료 제공.',
  url: canonicalUrl(route.fullPath),
});

const { getBirthTimeTxt } = fiveMixin();
const {
  formatNumber,
  getGenderTxt,
  getDayOfWeekOne,
  formatOption1Txt,
  formatOption2Txt,
  formatOption4Txt,
} = useFunctions();

/** 로딩 인디케이터 상태 */
const isLoading = ref(false);

const errorMessage = ref('');

// =================== 반응형 breakpoint ===================
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value <= 768);

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

// [반응형] iOS Safari 스크롤 반동(bounce) 제거
const syncOverscrollBehavior = () => {
  document.documentElement.style.overscrollBehavior = isMobile.value ? 'none' : '';
};

// 모바일 사이드바 drawer 상태
const isMobileSidebarOpen = ref(false);
const selectedSidebarTab = ref('list');

// 모바일 팝업 상태
const isPopHowtoOpen = ref(false);
const isPopUsageOpen = ref(false);

/** 모바일 사이드바 열기 */
const openMobileSidebar = () => {
  isMobileSidebarOpen.value = true;
};

/** 모바일 사이드바 닫기 */
const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
};

/** 모바일 사이드바 탭 선택 */
const selectSidebarTab = (tab) => {
  selectedSidebarTab.value = tab;
};

/** 로그인 페이지 이동 (모바일용) */
const goLogin = () => {
  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
};

/** 사주 입력 페이지 이동 (모바일용) */
const handleGoManse = () => {
  router.push('/saju-input');
};

/** 포인트 충전 페이지 이동 (모바일용) */
const goRecharge = () => {
  router.push('/purchase/points');
};

/** 사용 가이드 페이지 이동 (모바일용) */
const handleMobileGuide = (data) => {
  const { action, route } = data;
  if (action === 'route' && !!route?.tab) {
    if (route.tab === 'saju-input') handleGoManse();
    if (route.tab === 'saju-service') router.push('/saju-service');
  }
};

// =================== 하단 레이블 (모바일 브라우저) ===================
/** CalendarBottomLabel 일진보기 / 내사주선택 버튼 */
const startDailyFortune = (tab) => {
  selectSidebarTab(tab);
  openMobileSidebar();
};

const openSajuInfo = () => {
  openMobileSidebar();
};

const changeMySaju = () => {
  if (userId.value) {
    selectSidebarTab('list');
    openMobileSidebar();
  } else {
    handleLogin();
  }
};

/** 일진달력 선택 여부 - true: 일진달력, false: 역학달력 */
const isDailyFortuneCalendarOpen = ref(true);

// =================== 팝업 및 alert 관리 ===================
// '낭만일진 월 구매' 팝업 상태관리
const isPopMonthlyPurchase = ref(false);
const monthlyPurchaseRef = ref(null);

// '결제 방법 선택' 팝업 상태관리
const isPaymentPopupVisible = ref(false);
const selectedPackageInfo = ref(null); // { name, price, analysisTypeId }
const paymentPayload = ref(null); // { guestUserId, year, month }

/** '낭만일진 월 구매' 팝업 닫기 */
const closePurchaseMonthly = () => (isPopMonthlyPurchase.value = false);
/** 'M월 전체 일진 구매' 버튼 클릭 */
const clickPurchaseMonthly = () => {
  canUseDailyFortune();
  isPopMonthlyPurchase.value = true;
};

/** 패키지 선택 시 결제 방법 팝업 표시 */
const onSelectPackage = async ({ packageName, packagePrice, analysisTypeId }) => {
  const currentDate = refCalendar.value.calendar.currentData.currentDate;
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  selectedPackageInfo.value = {
    name: packageName,
    price: packagePrice,
    analysisTypeId,
  };

  paymentPayload.value = {
    guestUserId: mySajuData.value?.guestUserId,
    year,
    month,
  };

  // 낭만일진 분석권 상세 조회 (결제용 analysisTicketId 포함)
  await paymentsStore.fetchDailyFortuneTicketDetails();

  isPaymentPopupVisible.value = true;
};

/** 결제 팝업 닫기 */
const closePaymentPopup = () => {
  isPaymentPopupVisible.value = false;
  selectedPackageInfo.value = null;
  paymentPayload.value = null;
};

/** 포인트 결제 선택 시 */
const onPaymentPoint = (packageInfo) => {
  purchaseMonthly({
    packageName: packageInfo.name,
    packagePrice: packageInfo.price,
  });
};

/** 분석권 결제 성공 시 */
const onPaymentSuccess = async () => {
  closePaymentPopup();
  monthlyPurchaseRef?.value?.close();

  paymentsStore.getOwnPoint();
  paymentsStore.fetchTicketCount();
  triggerSnackbar('구매가 완료되었습니다.');

  const currentDate = refCalendar.value.calendar.currentData.currentDate;
  await getDailyFortuneCalendar(formatDate(currentDate, 'YYYY MM'));
  await refreshMySajuList();
  calendarApi.value.render();
};

/** 선택한 패키지에 맞는 분석권 수량 계산 */
const currentTicketCount = computed(() => {
  if (!selectedPackageInfo.value) return 0;
  // analysisTypeId: 35 = 풀팩, 36 = 미니팩
  if (selectedPackageInfo.value.analysisTypeId === 35) {
    return paymentsStore.dailyFullPackTicketCount;
  } else if (selectedPackageInfo.value.analysisTypeId === 36) {
    return paymentsStore.dailyMiniPackTicketCount;
  }
  return 0;
});

/** 선택한 패키지에 맞는 분석권 상세 데이터 */
const currentTicketData = computed(() => {
  if (!selectedPackageInfo.value) return null;
  if (selectedPackageInfo.value.analysisTypeId === 35) {
    return paymentsStore.dailyFortuneTicketDetails.fullPack;
  } else if (selectedPackageInfo.value.analysisTypeId === 36) {
    return paymentsStore.dailyFortuneTicketDetails.miniPack;
  }
  return null;
});

/** 분석권 이름 */
const currentTicketName = computed(() => {
  if (!selectedPackageInfo.value) return '일진 이용권';
  if (selectedPackageInfo.value.analysisTypeId === 35) {
    return '일진 데일리 풀팩';
  } else if (selectedPackageInfo.value.analysisTypeId === 36) {
    return '일진 데일리 미니팩';
  }
  return '일진 이용권';
});

/** 로그인 알림창 표시 */
const showLoginConfirm = () => {
  showConfirm({
    title: '로그인 하시면',
    title2: '일진을 보실 수 있습니다.',
    text: '로그인 하러 가기',
    onConfirm: () =>
      router.push({
        path: '/auth/login',
        query: { to: route.fullPath },
      }),
  });
};
/** 포인트 충전 팝업 */
const showRechargeConfirm = () => {
  showConfirm({
    title: '보유 낭만 포인트가 부족합니다.',
    title2: '지금 바로 충전하시겠습니까?',
    text: '충전하기',
    onConfirm: () => {
      const path = '/purchase/points';
      const query = {
        next: '/calendar/fortune',
        shortageAmount: shortageAmount.value,
      };
      router.push({ path, query });
    },
  });
};

/** 내 사주 등록 알림창 표시 */
const showSelectConfirm = () => {
  let filteredSajuList = [];
  let title = '내 사주 등록 후,';
  let title2 = '일진을 보실 수 있습니다.';
  let text = '내 사주 등록';

  if (mySajuList.value && mySajuList.value.length > 0) {
    filteredSajuList = mySajuList.value.filter((saju) => {
      return !isUnknownBirthTime(saju.guestUserId) && !isFutureSaju(saju.guestUserId);
    });
  }

  if (filteredSajuList && filteredSajuList.length > 0) {
    title = '내 사주를 선택하여,';
    title2 = '일진을 확인하세요.';
    text = '확인';
  }

  let onConfirm = () => {
    if (!mySajuList.value || filteredSajuList.length === 0) {
      router.push('/saju-input');
    } else {
      // 사주가 존재하는 경우에 할 행동 (예: 팝업 닫기 또는 다음 단계 진행)
    }
  };

  showConfirm({ title, title2, text, onConfirm });
};

const showUnknownTimeConfirm = () => {
  showAlert({
    title: `시간 모름인 사주는\n일진서비스 제공이 어렵습니다.`,
    //text: '확인',
    onConfirm: () => {},
  });
};

const showFutureSajuConfirm = () => {
  showAlert({
    title: `내년 이후 사주는\n일진서비스 제공이 어렵습니다.`,
    //text: '확인',
    onConfirm: () => {},
  });
};

// =================== 낭만일진 Sidebar ===================
/** 사주목록 내 케밥 메뉴에 연결된 guestUserId */
const openSettingId = ref(null);

/** 사주 설정 팝업 클릭 */
const toggleSettingPopup = (guestUserId) => {
  if (!guestUserId || !validateMySaju(guestUserId)) return;

  if (openSettingId.value === guestUserId) {
    openSettingId.value = null;
  } else {
    openSettingId.value = guestUserId;
  }
};

/* 대표설정 */
const setMainSetting = async (guestUserId, isDefaultVal) => {
  try {
    const params = {
      guestUserId: guestUserId,
      serviceCategory: 'DAILY_FORTUNE_CALENDAR',
      isDefault: isDefaultVal,
      ...(isDefaultVal && { isActive: true }),
    };
    await setMySajuAPI(params);
    await refreshMySajuList();
    toggleSettingPopup(null);

    if (isDefaultVal) triggerSnackbar(`대표 사주로 설정되었습니다.`);
    else triggerSnackbar(`대표 사주가 해제되었습니다.`);
  } catch (e) {
    console.error('대표설정 에러:', e);
  }
};

const beforeSetActive = (guestUserId, showSnackbar) => {
  if (!guestUserId || !validateMySaju(guestUserId)) return;

  showConfirm({
    title: '선택한 사주로 일진을 보시겠습니까?',
    text: '확인',
    onConfirm: () => setActiveSetting(guestUserId, showSnackbar),
  });
};

/* isActive 선택된 사주 update */
const setActiveSetting = async (guestUserId, showSnackbar = true) => {
  // '일진 보는중' 설정 요청
  try {
    isLoading.value = true; // 로딩 표시로 UI 깜빡임 방지

    const params = {
      guestUserId: guestUserId,
      serviceCategory: 'DAILY_FORTUNE_CALENDAR',
      isActive: true,
    };
    await setMySajuAPI(params);
    await selectMySaju(guestUserId);
    await refreshMySajuList();
    await getDailyFortuneCalendar(viewTitle.value);
    showSnackbar && triggerSnackbar(`선택한 사주로 일진을 볼 수 있어요.`);
  } catch (e) {
    console.error('내 사주 선택 에러:', e);
  } finally {
    isLoading.value = false;
  }
};

/* 내 사주 그룹 및 내 사주 새로고침 */
const refreshMySajuList = async () => {
  const id = userId.value;
  const yearMonth = convertViewTitleToYYYYMM(viewTitle.value);
  if (id && yearMonth) await fetchMySajuList(id, yearMonth);
};

// =================== 일진보기 Drawer ===================
/** 일진보기 drawer 열림 상태 */
const isDailyFortuneDrawerOpen = ref(false);

// 일진보기 데이터 요청 상태
const isDailyFortuneLoading = ref(false);
const isDailyFortuneLoaded = ref(false);

/** 일진보기 선택된 날짜의 일진달력 데이터 */
const dailyFortuneData = ref(null);
/** 일진보기 선택된 날짜 정보 */
const selectedDateInfo = ref(null);

/** 일진보기 drawer 열기 */
const openDailyFortuneDrawer = async ({ info, actionType }) => {
  if (info === null) return;
  if (!userId.value) return;

  const date = actionType === 'opened' ? info.day : info.date;
  const dataKey = formatDate(date, 'YYYYMMDD');
  const activeDate = formatDate(date);

  dailyFortuneData.value = monthlyFortuneData.value[dataKey];
  selectedDateInfo.value = date;

  sendDailyFortuneLog({
    dailyStatus: monthlyFortuneStatus.value[dataKey],
    isToday: todayDate.value === activeDate,
  });

  // 날짜 클릭 빈도 (무료/유료별) — 열람 여부와 무관하게 매 클릭 발화 (option3=클릭한 날짜)
  const clickedDailyStatus = monthlyFortuneStatus.value[dataKey];
  addContentLogAPI({
    contentType: clickedDailyStatus?.is_paid ? 'fc_day_click_paid_opt3' : 'fc_day_click_free_opt3',
    option3: activeDate,
  });

  try {
    isCategoryFetching.value = true;
    if (actionType === 'opened') {
      isDailyFortuneDrawerOpen.value = true;
      getDailyCategoryFreeCount(activeDate);
      await getDailyFortuneDetail({ activeDate });
      updateDailyFortuneCalendarStatus(formatDate(date, 'YYYYMM'));
      if (dailyCategoryStatus.value && !dailyCategoryStatus.value?.애정운?.is_checked) {
        addContentLogAPI({
          contentType: 'fc_luck_category_free_opt3',
          option3: '애정운',
        });
      }
      // 일진보기 열릴 때 기본으로 선택되는 생활운세
      await getDailyCategoryDetail(activeDate, '애정운');
    } else if (actionType !== 'past') {
      calendarApi.value.render();
    } else {
      shortageAmount.value = dailyFortunePrice.value - paymentsStore.points;
      showPointDialog({
        title: formatDate(date, 'YYYY년 MM월 DD일'),
        subtitle: '운세를 구매 하시겠습니까?',
        point: formatNumber(dailyFortunePrice.value),
        confirmText: '구매',
        onConfirm: async () => await confirmPurchaseFortune({ activeDate, dataKey }),
      });
    }
  } catch (e) {
  } finally {
    isCategoryFetching.value = false;
  }
};

/** 일진보기 drawer 닫기 */
const closeDailyFortuneDrawer = () => {
  if (isDailyFortuneDrawerOpen.value) {
    isDailyFortuneDrawerOpen.value = false;
    resetDailyFortuneData();
  }
};

/** 일진보기 열람 타입별 로그 전송 */
const sendDailyFortuneLog = ({ dailyStatus, isToday }) => {
  if (!dailyStatus) return;

  let contentType = '';
  const option3 = dailyStatus.is_paid ? '유료' : '무료';

  if (isToday && !dailyStatus.is_viewed_today) {
    contentType = 'fc_day_today_opt3';
  } else if (!isToday && dailyStatus.is_active && !dailyStatus.is_checked) {
    contentType = 'fc_day_not_today_opt3';
  }

  if (contentType) {
    addContentLogAPI({ contentType, option3 });
  }
};

/** 날짜별 무료 열람 가능한 카테고리 갯수 요청 */
const getDailyCategoryFreeCount = async (activeDate) => {
  try {
    const result = await getDailyCategoryFreeCountAPI({ activeDate });
    freeCategoryCount.value = result;
  } catch (e) {
    console.error('카테고리 별 운세(생활운세) 무료 카운트 조회 실패:', e);
  }
};

/** 일진보기 상세 데이터 요청 */
const getDailyFortuneDetail = async ({ activeDate, displayLoading = true }) => {
  if (displayLoading) {
    isDailyFortuneLoading.value = true;
    isDailyFortuneLoaded.value = false;
  }

  try {
    const result = await getDailyFortuneDetailAPI({ activeDate });

    if (result) {
      dailyFortuneDetail.value = result.data;
      dailyCategoryStatus.value = result.status;
      if (displayLoading) isDailyFortuneLoaded.value = true;
    } else {
      if (displayLoading) isDailyFortuneLoaded.value = false;
    }
  } catch (e) {
    DEV_MODE && console.error('일운보기 상세 데이터 조회 실패:', e);
    errorMessage.value = e?.response?.data.detail || e;
    if (displayLoading) isDailyFortuneLoaded.value = false;
  } finally {
    if (displayLoading) isDailyFortuneLoading.value = false;
    isCategoryLoading.value = false;
  }
};

/** 일진보기 클릭하여 열람 또는 구매했을 때, 변경된 상태값 조회 */
const updateDailyFortuneCalendarStatus = async (yyyymm) => {
  const totalStatus = await getDailyFortuneCheckAPI();

  monthlyFortuneStatus.value = Object.fromEntries(
    Object.entries(totalStatus).filter(([key]) => key.startsWith(yyyymm)),
  );

  calendarApi.value.render();
};

// =================== 낭만일진 구매 로직 ===================
/** 전체 일진 구매 / 패키지 이용중 버튼 텍스트 */
const purchaseButtonText = computed(() => {
  const title = viewTitle.value?.split('년')[1] || '';
  const status = parsedMySajuInfo?.value?.paidType;

  if (status === 'MONTH') return `${title} 미니팩 이용중`;
  if (status === 'MONTH_FULL') return `${title} 풀팩 이용중`;

  return title ? `${title} 전체 일진 구매` : '월 전체 일진 구매';
});

/** 낭만일진 구매 로직 함수 */
const purchaseDailyFortune = async ({ activeDate, purchaseType, callback = async () => {} }) => {
  isPurchasePending.value = true;

  const [year, month, day] = activeDate.split('-');
  const params = {
    year, // 필수
    month, // 필수
    purchaseType, // "DAY", "MONTH"(미니팩), "MONTH_FULL"(풀팩) : (필수)
    ...(purchaseType === 'DAY' && { day }), // purchaseType: "DAY" 일 때
  };

  const snackbarMessage =
    purchaseType === 'DAY' ? '운세가 구매되었습니다.' : '구매가 완료되었습니다.';

  try {
    const result = await purchaseDailyFortuneAPI(params);

    if (result) {
      paymentsStore.getOwnPoint();
      triggerSnackbar(snackbarMessage);
      await getDailyFortuneCalendar(`${year} ${month}`);
      await callback();
      calendarApi.value.render();
    }
  } catch (e) {
    console.error('결제 실패:', e?.response?.data?.detail);
    handlePurchaseError(e);
  } finally {
    isPurchasePending.value = false;
  }
};

const shortageAmount = ref(0);
/** 달 전체 일진 구매 */
const purchaseMonthly = ({ packageName, packagePrice }) => {
  canUseDailyFortune();

  shortageAmount.value = packagePrice - paymentsStore.points;

  const currentDate = refCalendar.value.calendar.currentData.currentDate;
  const purchaseType = packageName === '데일리 미니팩' ? 'MONTH' : 'MONTH_FULL';

  showPointDialog({
    title: formatDate(currentDate, 'YYYY년 MM월'),
    subtitle: `낭만일진 ${packageName}을\n 구매 하시겠습니까?`,
    point: formatNumber(packagePrice),
    confirmText: '구매',
    onConfirm: async () => {
      await purchaseDailyFortune({
        activeDate: formatDate(currentDate),
        purchaseType,
        callback: async () => {
          isPaybackUser.value && (await getDailyFortunePrice());
          monthlyPurchaseRef?.value?.close();
          await refreshMySajuList();
        },
      });
    },
  });
};

/** 일진보기 결제 */
const confirmPurchaseFortune = async ({ activeDate, dataKey }) => {
  await purchaseDailyFortune({
    activeDate,
    purchaseType: 'DAY',
    callback: async () => {
      openDailyFortuneDrawer({ info: { day: activeDate }, actionType: 'opened' });
      addContentLogAPI({
        contentType: 'fc_day_not_today_opt3',
        option3: '유료',
      });
    },
  });
};

// =================== 생활운세 구매 로직 ===================
const isCategoryFetching = ref(false);

/** '카테고리 별 운세(생활운세)' 버튼 클릭 */
const clickCategoryButton = async (label) => {
  sendCategoryFortuneLog(label);
  isCategoryFetching.value = true;

  const formattedSelectedDate = formatDate(selectedDateInfo.value);

  try {
    if (dailyCategoryStatus.value && dailyCategoryStatus.value[label]['is_active']) {
      // 열어본 운세일 때,
      await getDailyCategoryDetail(formattedSelectedDate, label);
    } else {
      // 열어보지 않은 운세일 때,
      if (selectedCategoryList.value.length < freeCategoryCount.value) {
        // 무료 열람일 때,
        await getDailyCategoryDetail(formattedSelectedDate, label);
      } else {
        shortageAmount.value = categoryPrice.value - paymentsStore.points;
        // 무료 열람가능 개수를 넘었을 때,
        showPointDialog({
          title: label.replace('학습', '학업'),
          subtitle: '운세를 구매 하시겠습니까?',
          point: formatNumber(categoryPrice.value),
          confirmText: '구매',
          onConfirm: async () => {
            await purchaseDailyCategory({
              activeDate: formattedSelectedDate,
              category: label,
              categoryPrice: categoryPrice.value,
            });
          },
        });
      }
    }
  } catch (error) {
  } finally {
    isCategoryFetching.value = false;
  }
};

/** 생활운세 유형 별 로그 전송 */
const sendCategoryFortuneLog = (label) => {
  const category = dailyCategoryStatus.value?.[label];
  // is_checked(열람 여부)와 무관하게 카테고리 클릭 시마다 발화 (생활운세 클릭 빈도 집계).
  // 무료/유료 = 분석내역(구매) 보유 여부로 판정. 미구매는 레코드/활성/무료한도 무관하게 무료로 +1.
  const contentType = category?.is_paid
    ? 'fc_luck_category_paid_opt3'
    : 'fc_luck_category_free_opt3';
  addContentLogAPI({
    contentType,
    option3: label,
  });
};

/** '카테고리 별 운세(생활운세)' 구매 */
const purchaseDailyCategory = async ({ activeDate, category, categoryPrice }) => {
  isPurchasePending.value = true;

  const params = {
    activeDate, // yyyy-mm-dd
    category, // "STUDY", "WORK", "WEALTH", "LOVE", "HEALTH", "FAMILY", "MOVE", "RELATIONSHIP", "FAME", "SOCIAL"
    categoryPrice, // 차감 포인트
  };

  try {
    const result = await purchaseDailyCategoryAPI(params);
    if (result) {
      paymentsStore.getOwnPoint();
      triggerSnackbar('운세가 구매되었습니다.');
      await getDailyCategoryDetail(activeDate, category);
      setSelectedCategory(category);
      addContentLogAPI({
        contentType: 'fc_luck_category_paid_opt3',
        option3: category,
      });
    }
  } catch (e) {
    DEV_MODE && console.error('카테고리 별 운세(생활운세) 구매 실패', e);
    handlePurchaseError(e);
  } finally {
    isPurchasePending.value = false;
  }
};

/** 스낵바 */
const { snackbarRef, triggerSnackbar } = useSnackbar();

/** 화면 내 팝업 관리 */
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogSubtitle,
  dialogNextUrl,
  displayPoint,
  alertRef,
  confirmRef,
  pointDialogRef,
  showAlert,
  showConfirm,
  showPointDialog,
} = useDialogManager();

/** 사주시계 관리 */
const { sajuClockRef, isSajuClockOpen, setSajuClockOpen } = useSajuClock();

/** '내 사주' 및 '내 사주 목록' 데이터 관리 로직 */
const {
  mySajuData,
  mySajuList,
  isMySajuDataExist,
  isMySajuListExist,
  isMySajuLoading,
  isMySajuLoaded,
  fetchMySajuList,
  selectMySaju,
} = useMySaju({
  userId,
  errorMessage,
});

/** 파싱된 내 사주 정보 */
const parsedMySajuInfo = useParsedMySajuInfo({
  mySajuData,
  mySajuList,
  isMySajuDataExist,
  utils: {
    formatDate,
    getGenderTxt,
    getDayOfWeekOne,
    formatOption1Txt,
    formatOption2Txt,
    formatOption4Txt,
    getBirthTimeTxt,
  },
});

/** '내 사주' 선택 유효성 검증 로직 */
const { isUnknownBirthTime, isFutureSaju, validateMySaju } = useMySajuValidator({
  mySajuList,
  onUnknownTime: () => showUnknownTimeConfirm(),
  onFutureSaju: () => showFutureSajuConfirm(),
});

/** 일진달력 가격 조회 */
const {
  dailyFortunePrice,
  categoryPrice,
  monthlyFortunePrice,
  monthlyFullFortunePrice,
  isPaybackUser,
  getDailyFortunePrice,
} = useServicePrice();

/** 일진달력 결제 관리 */
const { isPurchasePending, handlePurchaseError } = usePurchaseFortune({
  showRechargeConfirm,
});

/** 유효성 검사 */
const { canUseDailyFortune } = useCalendarValidation({
  userId,
  isMySajuDataExist,
  showLoginConfirm,
  showSelectConfirm,
  triggerSnackbar,
});

/** 달력 생성 */
const {
  calendarApi,
  calendarOptions,
  refCalendar,
  viewTitle,
  isToday,
  firstSeason,
  firstSeasonMonth,
  todayDate,
  selectedDayCellProps,
  monthlyFortuneData,
  monthlyFortuneStatus,
  getDailyFortuneCalendar,
  navigateCalendar,
} = useCalendar({
  calendarType: computed(() => (isMobile.value ? 'mobile' : 'web')),
  isLoading,
  isMySajuLoading,
  isMySajuLoaded,
  isDailyFortuneCalendarOpen,
  userId,
  isSajuDataExist: isMySajuDataExist,
  dailyFortunePrice,
  canUseDailyFortune,
  openDailyFortuneDrawer,
});

/** 달력 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: () => navigateCalendar({ type: 'next' }),
  onSwipeRight: () => navigateCalendar({ type: 'prev' }),
});

/** 일진보기 데이터 관리 */
const {
  freeCategoryCount,
  dailyFortuneDetail,
  dailyCategoryStatus,
  luckButtonDisabled,
  selectedCategoryList,
  selectedCategory,
  dailyCategoryDetail,
  lifeLuckChartData,
  isCategoryLoading,
  isCategoryLoaded,
  setSelectedCategory,
  getDailyCategoryDetail,
  resetDailyFortuneData,
} = useDailyFortune();

/** 일진보기 상세 데이터 객체 */
const parsedDailyFortune = useParsedDailyFortune(dailyFortuneDetail, selectedDayCellProps);

const fetchUserStoreData = () => {
  paymentsStore.fetchPointsAndTickets();
  primarySajuStore.fetchPrimaryAndMySaju();
};

onMounted(async () => {
  window.addEventListener('resize', updateWindowWidth);
  syncOverscrollBehavior();

  getDailyFortunePrice();
  if (userId.value) fetchUserStoreData();
});

// 로그인 상태 변경 시 데이터 재로드
watch(userId, async (newId, oldId) => {
  if (newId && !oldId) {
    await refreshMySajuList();
    fetchUserStoreData();
  } else if (!newId && oldId) {
    clearMySajuState();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth);
  document.documentElement.style.overscrollBehavior = '';
});

// [반응형] isMobile 변경 시 FullCalendar 리렌더링
watch(isMobile, () => {
  nextTick(() => {
    calendarApi.value?.render();
    syncOverscrollBehavior();
  });
});

watch(viewTitle, async (newTitle) => {
  if (newTitle) await refreshMySajuList();
});

// 사주 데이터 존재 여부 변경 시 일진달력 데이터 요청
watch(isMySajuDataExist, (isExist) => {
  if (isExist && isDailyFortuneCalendarOpen.value) {
    getDailyFortuneCalendar(viewTitle.value);
  }
});

watch(
  () => isDailyFortuneDrawerOpen.value,
  (isOpen) => {
    if (!isOpen) {
      // 일진 보기 닫을 때 초기화
      setSelectedCategory(null);
    }
  },
);

/** 열려있는 팝업/모달 상태 확인 */
const isAnyPopupOpen = computed(
  () =>
    isDailyFortuneDrawerOpen.value ||
    isLoading.value ||
    (isPopMonthlyPurchase.value && parsedMySajuInfo.value) ||
    (alertRef.value?.isAlertDialogVisible ?? false) ||
    (confirmRef.value?.isConfirmDialogVisible ?? false) ||
    (pointDialogRef.value?.isConfirmDialogVisible ?? false),
);
/** 스크롤 제어 */
const {} = useCustomScrollLock({ activeScrollLock: isAnyPopupOpen });

const isWebView = computed(() => typeof window !== 'undefined' && !!window.ReactNativeWebView);

/** 달력 시트 높이 (모바일 브라우저: 동적, 그 외: 700px) */
const calendarSheetHeight = computed(() => {
  if (isMobile.value && !isWebView.value) {
    // MobileHeader(52px) + CalendarHeaderMobile(~48px) = 100px
    return '500';
  }
  return '700';
});

useBackLayer({
  id: 'fortune-daily-detail',
  isOpen: isDailyFortuneDrawerOpen,
  closeFromBack: closeDailyFortuneDrawer,
});
useBackLayer({
  id: 'fortune-saju-sidebar',
  isOpen: isMobileSidebarOpen,
  closeFromBack: closeMobileSidebar,
});
useBackLayer({
  id: 'fortune-monthly-purchase',
  isOpen: computed(() => isPopMonthlyPurchase.value && !!parsedMySajuInfo.value),
  closeFromBack: closePurchaseMonthly,
});
useBackLayer({
  id: 'fortune-payment',
  isOpen: isPaymentPopupVisible,
  closeFromBack: closePaymentPopup,
});
useBackLayer({
  id: 'fortune-saju-clock',
  isOpen: isSajuClockOpen,
  closeFromBack: () => setSajuClockOpen(false),
});
useBackLayer({
  id: 'fortune-howto',
  isOpen: isPopHowtoOpen,
  closeFromBack: () => {
    isPopHowtoOpen.value = false;
  },
});
useBackLayer({
  id: 'fortune-usage',
  isOpen: isPopUsageOpen,
  closeFromBack: () => {
    isPopUsageOpen.value = false;
  },
});
useBackLayer({
  id: 'fortune-loading',
  isOpen: isLoading,
  dismissible: false,
});
</script>

<template>
  <div class="calendar-wrap" :class="isMobile ? 'mobile' : 'v2'">
    <!-- Mobile: Header (앱·모바일웹 모두 숨김 — 하단 탭바로 대체) -->
    <MobileHeader v-if="!isMobile" title="낭만일진" :show-menu="true" />

    <!-- PC: CreditBar -->
    <CreditBar
      v-if="!isMobile"
      :userId="userId"
      :show-back="isWebView"
      :show-menu="!isWebView"
      @back="handleBack"
    />

    <DailyFortuneDrawer
      v-show="isDailyFortuneDrawerOpen"
      v-model:isDailyFortuneDrawerOpen="isDailyFortuneDrawerOpen"
      :selectedDayCellProps="selectedDayCellProps"
      :isDailyFortuneLoading="isDailyFortuneLoading"
      :isDailyFortuneLoaded="isDailyFortuneLoaded"
      :dailyCustomObject="parsedDailyFortune"
      :dailyFortuneDetail="dailyFortuneDetail"
      :sajuData="mySajuData"
      :lifeLuckChartData="lifeLuckChartData"
      :dailyFortuneData="dailyFortuneData"
      :isCategoryLoading="isCategoryLoading"
      :isCategoryLoaded="isCategoryLoaded"
      :categoryPrice="categoryPrice"
      :selectedCategory="selectedCategory"
      :dailyCategoryDetail="dailyCategoryDetail"
      :dailyCategoryStatus="dailyCategoryStatus"
      :isCategoryFetching="isCategoryFetching || isPurchasePending"
      :luckButtonDisabled="luckButtonDisabled"
      @clickCategoryButton="clickCategoryButton"
      @closeDrawer="closeDailyFortuneDrawer"
    />

    <!-- 모바일: 사이드바 Drawer -->
    <MobileCalendarDrawer v-if="isMobile" v-model="isMobileSidebarOpen">
      <MobileCalendarDrawerHeader
        :selectedTab="selectedSidebarTab"
        @select="selectSidebarTab"
        @close="closeMobileSidebar"
      />
      <MobileCalendarDrawerBody
        :selectedSidebarTab="selectedSidebarTab"
        :mySajuList="mySajuList"
        :isLoading="isLoading"
        :isDailyFortuneCalendarOpen="isDailyFortuneCalendarOpen"
        :isSajuDataExist="isMySajuDataExist"
        :userId="userId"
        :mySajuObject="parsedMySajuInfo"
        :mySajuMonthlyStatus="parsedMySajuInfo?.paidType"
        :sajuganjiArray="parsedMySajuInfo?.sajuganjiArray"
        :sajuganjiKorArray="parsedMySajuInfo?.sajuganjiKorArray"
        :openSettingId="openSettingId"
        :viewTitle="viewTitle"
        :paymentsStore="paymentsStore"
        :viewingLabel="'일진 보는중'"
        :hideMonthlyBadge="false"
        :showTaegilBadge="false"
        :serviceName="'일진'"
        :primarySajuId="primarySajuStore.primarySajuId"
        :mySajuId="primarySajuStore.mySajuId"
        @clickService="() => {}"
        @clickRecharge="goRecharge"
        @clickPurchaseMonthly="clickPurchaseMonthly"
        @toggleSettingPopup="toggleSettingPopup"
        @setMainSetting="setMainSetting"
        @setActiveSetting="beforeSetActive"
        @selectListTab="() => selectSidebarTab('list')"
        @refreshMySajuList="refreshMySajuList"
        @handleGoManse="handleGoManse"
        @openPopHowto="() => (isPopHowtoOpen = true)"
        @openPopUsage="() => (isPopUsageOpen = true)"
        @sendMessageToApp="handleMobileGuide"
        @goLogin="goLogin"
      />
    </MobileCalendarDrawer>

    <div class="content-main fortune" :class="{ 'mb-large': !isMobile, webview: isWebView }">
      <section class="inner-base-calendar v2">
        <div v-if="!isMobile" style="height: 20px"></div>
        <div class="calendar-sidebar-layout">
          <!-- PC: CalendarSidebar -->
          <CalendarSidebar
            v-if="!isMobile"
            :isDailyFortuneCalendarOpen="isDailyFortuneCalendarOpen"
            :sajuData="mySajuData"
            :isSajuDataExist="isMySajuDataExist"
            :mySajuObject="parsedMySajuInfo"
            :mySajuList="mySajuList"
            :openSettingId="openSettingId"
            :primarySajuId="primarySajuStore.primarySajuId"
            :mySajuId="primarySajuStore.mySajuId"
            @setMainSetting="setMainSetting"
            @setActiveSetting="beforeSetActive"
            @refreshMySajuList="refreshMySajuList"
            @toggleSettingPopup="toggleSettingPopup"
            @selectMySaju="selectMySaju"
          />

          <div class="calendar-main">
            <!-- PC: CalendarHeader -->
            <CalendarHeader
              v-if="!isMobile"
              :calendarType="'v2'"
              :title="viewTitle"
              :isToday="isToday"
              :solarTerm="firstSeason"
              :solarTermMonth="firstSeasonMonth"
              :isColorChecked="true"
              @prev="navigateCalendar({ type: 'prev' })"
              @next="navigateCalendar({ type: 'next' })"
              @today="navigateCalendar({ type: 'today' })"
              @clock="setSajuClockOpen(true)"
            />

            <!-- 모바일: CalendarHeaderMobile -->
            <CalendarHeaderMobile
              v-if="isMobile"
              :calendarType="'mobile'"
              :title="viewTitle"
              :isToday="isToday"
              :solarTerm="firstSeason"
              :solarTermMonth="firstSeasonMonth"
              :isColorChecked="true"
              :isDailyFortuneCalendarOpen="isDailyFortuneCalendarOpen"
              @prev="navigateCalendar({ type: 'prev' })"
              @next="navigateCalendar({ type: 'next' })"
              @today="navigateCalendar({ type: 'today' })"
              @clock="setSajuClockOpen(true)"
              @option="() => {}"
              @sidebar="openMobileSidebar"
            />

            <div class="sec-sch-body" :class="{ mobile: isMobile }">
              <v-row
                class="fill-height"
                @mousedown="handleStart"
                @mousemove="handleMove"
                @mouseup="handleEnd"
                @touchstart="handleStart"
                @touchmove="handleMove"
                @touchend="handleEnd"
              >
                <v-col>
                  <v-sheet :height="calendarSheetHeight" style="position: relative">
                    <div
                      v-if="isDailyFortuneCalendarOpen && !isMobile"
                      class="img-nangmanee"
                      :class="{ active: parsedMySajuInfo?.paidType }"
                    ></div>
                    <button
                      v-if="isDailyFortuneCalendarOpen && !isMobile"
                      class="purchase-tab"
                      :class="{ active: parsedMySajuInfo?.paidType }"
                      :disabled="parsedMySajuInfo?.paidType"
                      @click="clickPurchaseMonthly"
                    >
                      <IconCalendarFill class="icon-calendar" />
                      {{ purchaseButtonText }}
                      <i class="purchase-tab__status-icon"></i>
                    </button>
                    <FullCalendar
                      ref="refCalendar"
                      class="full-calendar web"
                      :class="{ webview: isWebView }"
                      :options="calendarOptions"
                    />
                  </v-sheet>
                </v-col>
              </v-row>
            </div>

            <CalendarBottomLabel
              v-if="isMobile"
              :isDailyFortuneCalendarOpen="isDailyFortuneCalendarOpen"
              :isSajuDataExist="isMySajuDataExist"
              :mySajuObject="parsedMySajuInfo"
              :isMySajuListExist="isMySajuListExist"
              :paymentsStore="paymentsStore"
              :viewTitle="viewTitle"
              :mySajuMonthlyStatus="parsedMySajuInfo?.paidType"
              @startDailyFortune="startDailyFortune"
              @openSajuInfo="openSajuInfo"
              @changeMySaju="changeMySaju"
              @clickRecharge="goRecharge"
              @clickPurchaseMonthly="clickPurchaseMonthly"
            />
          </div>
        </div>
      </section>
    </div>

    <SajuClockPopup
      v-if="isSajuClockOpen"
      ref="sajuClockRef"
      @closeClock="setSajuClockOpen(false)"
    />

    <!-- PC: 월운 구매 팝업 -->
    <PopPurchaseMonthly
      v-if="isPopMonthlyPurchase && parsedMySajuInfo && !isMobile"
      ref="monthlyPurchaseRef"
      :viewTitle="viewTitle"
      :mySajuObject="parsedMySajuInfo"
      :monthlyFortunePrice="monthlyFortunePrice"
      :monthlyFullFortunePrice="monthlyFullFortunePrice"
      :isPaybackUser="isPaybackUser"
      @close="closePurchaseMonthly"
      @purchaseMonthly="purchaseMonthly"
      @selectPackage="onSelectPackage"
    />

    <!-- Mobile: 월운 구매 팝업 -->
    <PopPurchaseMonthlyMobile
      v-if="isPopMonthlyPurchase && parsedMySajuInfo && isMobile"
      ref="monthlyPurchaseRef"
      :viewTitle="viewTitle"
      :mySajuObject="parsedMySajuInfo"
      :monthlyFortunePrice="monthlyFortunePrice"
      :monthlyFullFortunePrice="monthlyFullFortunePrice"
      :isPaybackUser="isPaybackUser"
      :isPaymentPopupOpen="isPaymentPopupVisible"
      @close="closePurchaseMonthly"
      @purchaseMonthly="purchaseMonthly"
      @selectPackage="onSelectPackage"
    />

    <DailyFortunePaymentPopup
      :isVisible="isPaymentPopupVisible"
      :packageInfo="selectedPackageInfo"
      :payload="paymentPayload"
      :ticketCount="currentTicketCount"
      :ticketCountData="currentTicketData"
      :ticketName="currentTicketName"
      @close="closePaymentPopup"
      @payment-point="onPaymentPoint"
      @payment-success="onPaymentSuccess"
    />

    <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" />

    <ConfirmComponent
      ref="confirmRef"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />

    <PointConfirm
      ref="pointDialogRef"
      :title="dialogTitle"
      :subtitle="dialogSubtitle"
      :point="displayPoint"
      :nextUrl="dialogNextUrl"
      :confirm-text="'구매'"
    />

    <Snackbar ref="snackbarRef" />

    <LoadingIndicator v-if="isLoading" />

    <!-- 모바일 팝업: 일진 활용법 -->
    <PopCalendarHowtoMobile v-if="isMobile && isPopHowtoOpen" @close="isPopHowtoOpen = false" />

    <!-- 모바일 팝업: 일진 사용법 -->
    <PopCalendarUsageMobile v-if="isMobile && isPopUsageOpen" @close="isPopUsageOpen = false" />
  </div>
</template>
